import React from 'react';
import styled from 'styled-components';
import { useStoreState, useStoreActions } from '../store';
import { useLocale } from '../hooks/useLocale';

export interface ResumeOverlayProps {
  children: React.ReactNode;
}

export function ResumeOverlay(props: ResumeOverlayProps) {
  const locale = useLocale();
  const resumeOverlayVisible = useStoreState(state => state.player.resumeOverlayVisible);
  const playbackResumePoint = useStoreState(state => state.player.playbackResumePoint);
  const castSession = useStoreState(state => state.chromecast.castSession);
  const data = useStoreState(state => state.videos.data);
  const actions = useStoreActions(actions => actions);

  async function handleResumeClick() {
    actions.player.seek(playbackResumePoint);
    actions.player.setResumeOverlayVisible(false);

    if (castSession) {
      actions.chromecast.loadMedia();
    } else {
      actions.player.play();
    }
  }

  async function handleRestartClick() {
    actions.player.setResumeOverlayVisible(false);
    actions.player.seek(0);

    if (castSession) {
      actions.chromecast.loadMedia();
    } else {
      actions.player.play();
    }
  }

  return (
    <>
      {props.children}
      {resumeOverlayVisible && (
        <Content>
          <Title>{locale.translations.ResumeOverlay.AboutToPlay}</Title>
          <VideoTitle>{data?.metadata.title}</VideoTitle>
          <ResumeButton onClick={handleResumeClick}>{locale.translations.ResumeOverlay.ResumeVideo}</ResumeButton>
          <StartOverButton onClick={handleRestartClick}>{locale.translations.ResumeOverlay.StartOver}</StartOverButton>
        </Content>
      )}
    </>
  );
}

const Content = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-family: ProximaNova-Regular;
  background-color: #161819f2;
`;

const Title = styled.div`
  color: ${({ theme }) => theme.primaryBlue};
  text-transform: uppercase;
  font-family: 'Lato-Bold', sans-serif;
  font-size: 14px;
  font-weight: 600;
`;

const VideoTitle = styled.div`
  font-family: 'Lato-Black', sans-serif;
  font-size: 20px;
  font-weight: 900;
  text-align: center;
  color: ${({ theme }) => theme.white};
  margin: 6px 1em 40px;
  text-transform: capitalize;
`;

const ResumeButton = styled.div`
  font-family: 'Lato-Bold', sans-serif;
  font-weight: 600;
  text-align: center;
  color: ${({ theme }) => theme.white};
  border-radius: 20px;
  background-color: ${({ theme }) => theme.primaryBlue};
  text-transform: uppercase;
  padding: 10px 20px;
  border: 0;
  font-size: 14px;
  letter-spacing: 0.8px;
  line-height: 1.43;
  margin-bottom: 15px;
  cursor: pointer;
  outline: none;

  &:hover {
    filter: brightness(1.1);
  }
`;

const StartOverButton = styled(ResumeButton)`
  background-color: transparent;
  border: ${({ theme }) => `1px solid ${theme.primaryBlue}`};
  color: ${({ theme }) => theme.primaryBlue};
  margin: 0;
`;
